import { useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getCurrentUser, getCachedUser } from '../services/auth';
import { withTimeout } from '../services/network';
import { metaRepo } from '../services/db/meta';
import useStore from '../store';

// Pull the email out of the cached idToken. Apple Sign In (and any
// other federated-then-token-injected path) gives us a user with a
// UUID `username` and no `signInDetails.loginId`, so without this
// the Settings page would surface the raw Cognito sub.
async function enrichWithEmail(user) {
  if (!user) return user;
  if (user.signInDetails?.loginId) return user;
  if (typeof user.username === 'string' && user.username.includes('@')) {
    return { ...user, signInDetails: { ...(user.signInDetails || {}), loginId: user.username } };
  }
  try {
    const session = await fetchAuthSession();
    const claims = session?.tokens?.idToken?.payload || {};
    const email = claims.email || claims['cognito:username'];
    if (typeof email === 'string' && email.includes('@')) {
      return { ...user, signInDetails: { ...(user.signInDetails || {}), loginId: email } };
    }
  } catch {}
  return user;
}

export function useAuth() {
  const { user, authLoading, setUser, setAnonUserId } = useStore();

  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      try {
        // Always make sure an anonymous identity exists so the data layer has
        // something to scope by even before (or without) Cognito resolves.
        const anonId = await metaRepo.ensureAnonUserId();
        if (mounted) setAnonUserId(anonId);

        // Wrap the Cognito call in a timeout — Amplify hangs forever offline.
        // On timeout, fall back to the tokens Amplify persisted in localStorage
        // so a signed-in user staying offline is NOT demoted to anonymous (which
        // would hide their real habits and pop the anonymous banner).
        const currentUser = await withTimeout(getCurrentUser(), 4000, 'getCurrentUser timed out')
          .catch(() => getCachedUser());
        const enriched = currentUser ? await enrichWithEmail(currentUser) : null;
        if (mounted) setUser(enriched);
      } catch {
        if (mounted) setUser(null);
      }
    }

    checkAuth();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuth();
          break;
        case 'signedOut':
          setUser(null);
          break;
        default:
          break;
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [setUser, setAnonUserId]);

  return { user, loading: authLoading };
}
